import { FC, MouseEvent } from 'react'
import { IconButton, MenuItem, Typography } from '@mui/material'
import theme from 'theme'
import { useThemeColors } from 'hooks/useThemeColors'

/* Components */
import { MarkMenu } from '../listToolbar.styles'

interface Props {
  markMenuOpen: boolean
  anchorEl: null | HTMLElement
  handleClick: (event: MouseEvent<HTMLElement>) => void
  handleClose: (option: string) => void
  markMenuOptions: string[]
  selectedOption: string
}

const SortMenu: FC<Props> = ({ markMenuOptions, markMenuOpen, selectedOption, handleClick, handleClose, anchorEl }) => {
  const getColor = useThemeColors()
  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={markMenuOpen ? 'long-menu' : undefined}
        aria-expanded={markMenuOpen ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        disableRipple
        sx={{
          color: markMenuOpen ? theme.colors.textSecondary : theme.colors.cta,
          background: markMenuOpen ? theme.colors.analyzerIconActive : '',
          '&:hover': {
            background: theme.colors.analyzerIconHover,

            svg: {
              path: {
                fill: theme.colors.analyzerIconActive
              }
            }
          },
          "html[data-theme='light'] &": {
            '&:hover': {
              background: 'transparent',
              svg: { path: { fill: '#c477c1' } }
            },
            '&:focus': {
              svg: { path: { fill: theme.colors.buttonHollowFocus } }
            },
            '&:disabled': {
              svg: { path: { fill: theme.colors.btnSecondaryDisabledText } }
            }
          }
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M2 18.3333H8.66667V16.1111H2V18.3333ZM2 5V7.22222H22V5H2ZM2 12.7778H15.3333V10.5556H2V12.7778Z"
            fill={markMenuOpen ? getColor('text-primary') : getColor('cta')}
          />
        </svg>
      </IconButton>
      <MarkMenu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button'
        }}
        anchorEl={anchorEl}
        open={markMenuOpen}
        onClose={() => handleClose('')}
        sx={{
          '.MuiPaper-root': {
            marginTop: '5.5px',
            borderRadius: theme.attributes.borderRadius.small,
            width: '200px',
            maxHeight: '167px',
            border: `1px solid ${theme.colors.strokePrimary}`,
            background: theme.colors.frameworkCardBg,
            padding: 0,

            "html[data-theme='light'] &": {
              boxShadow: '0 4px 8px 0 rgba(162, 145, 158, 0.20)'
            },

            '.MuiList-root': {
              padding: `${theme.spacing.xSmall}px`,

              '.MuiMenuItem-root': {
                justifyContent: 'space-between',
                borderRadius: theme.attributes.borderRadius.small,
                height: '40px',
                overflow: 'hidden',
                color: theme.colors.foreground,
                textOverflow: 'ellipsis',
                fontSize: theme.typography.fontSize.xLarge,
                fontStyle: 'normal',
                fontWeight: theme.typography.fontWeight.normal,
                lineHeight: 'normal',
                padding: `0 ${theme.spacing.xSmall}px`,

                svg: {
                  transition: 'all .4s ease',

                  path: {
                    transition: 'all .4s ease'
                  }
                },

                '&:hover': {
                  background: theme.colors.tooltipBg,

                  "html[data-theme='light'] &": {
                    background: theme.colors.menuItemHover
                  }
                },

                '&.Mui-selected, &.Mui-focusVisible': {
                  "html[data-theme='light'] &": {
                    background: theme.colors.cardFillTertiary
                  }
                }
              }
            }
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Typography className="sort-menu-title">Sort by</Typography>
        {markMenuOptions.map((option) => (
          <MenuItem key={option} selected={false} onClick={() => handleClose(option)}>
            {option}
            {selectedOption === option && (
              <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.35588 16.5296L3.61455 11.7882L2 13.3914L8.35588 19.7473L22 6.10318L20.3968 4.5L8.35588 16.5296Z"
                  fill={getColor('cta')}
                />
              </svg>
            )}
          </MenuItem>
        ))}
      </MarkMenu>
    </div>
  )
}

export default SortMenu
